import type { OrderTrackingOrderSource } from "@redotech/order-tracking-common/order-tracking-types";
import type { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";
import {
  SUNDAYRED_TEAMS,
  TAYLORMADE_TEAMS,
} from "@redotech/redo-integrations-definitions-common/taylormade/constants";
import { Provider } from "@redotech/redo-order-common/provider";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { assertNever } from "@redotech/util/type";

interface ReturnAppSettings {
  storeUrl: string;
  merchantName: string;
  useShopifyStorefrontForCommentSold: boolean;
  exchanges: { shopSiteDomain: string; shopSiteURL: string };
}

export interface UpsellRedirectHandler {
  logUpsellAndRedirect: (selectedVariant?: {
    id: string;
    handle: string;
    productUrl?: string | null;
  }) => void;
}

export interface UpsellRedirectParams {
  order: TrackingPageOrder;
  selectWindow: React.MutableRefObject<Window>;
  returnAppSettings: ReturnAppSettings;
  logPageClick: (params: {
    trackableId: string;
    trackableType: TrackableType;
    eventType: string;
    url: string;
    image?: string;
  }) => Promise<void>;
  redirectToWebsiteWithDiscountCode?: (params: {
    discountCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmContent?: string;
    utmTerm?: string;
    settings: ReturnAppSettings;
    window: React.MutableRefObject<Window>;
    selectedProduct?: { url: string; variantId?: string };
  }) => void;
  discountCode?: string;
  redirectUrl?: string;
  /**
   * The provider the recommended products were actually fetched from. Only
   * consulted when `order.source` itself has no storefront to redirect to
   * (recommendations were rerouted to a capable provider); every source with
   * its own redirect handler keeps keying on `order.source`.
   */
  redirectProvider?: OrderTrackingOrderSource;
  /**
   * Destination of the CTA. "checkout" (default) keeps the existing
   * add-to-cart / checkout behavior. "pdp" sends shoppers to the
   * product detail page instead.
   */
  ctaDestination?: "checkout" | "pdp";
}

function hasStorefrontRedirect(source: OrderTrackingOrderSource): boolean {
  switch (source) {
    case Provider.SHOPIFY:
    case Provider.BIGCOMMERCE:
    case Provider.COMMENTSOLD:
    case Provider.COMMERCE_CLOUD:
    case Provider.SFOM:
      return true;
    case Provider.REDO:
    case Provider.REDO_API:
    case Provider.CUSTOM_RETURN_INTEGRATION:
    case Provider.NETSUITE:
    case Provider.APTOS:
      return false;
    default:
      return assertNever(source);
  }
}

export function createUpsellRedirectHandler(
  params: UpsellRedirectParams,
): UpsellRedirectHandler {
  const source = hasStorefrontRedirect(params.order.source)
    ? params.order.source
    : (params.redirectProvider ?? params.order.source);

  switch (source) {
    case Provider.SHOPIFY:
      return createShopifyRedirectHandler(params);
    case Provider.BIGCOMMERCE:
      return createBigCommerceRedirectHandler(params);
    case Provider.COMMENTSOLD:
      return createCommentsoldRedirectHandler(params);
    case Provider.COMMERCE_CLOUD:
    case Provider.SFOM:
      return createCommerceCloudRedirectHandler(params);
    case Provider.REDO:
    case Provider.REDO_API:
    case Provider.CUSTOM_RETURN_INTEGRATION:
    case Provider.NETSUITE:
    case Provider.APTOS:
      console.error("Upsell redirects are not supported for this provider", {
        source,
      });
      return { logUpsellAndRedirect: () => {} };
    default:
      assertNever(source);
  }
}

function createShopifyRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
  redirectToWebsiteWithDiscountCode,
  discountCode,
  redirectUrl,
  ctaDestination,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
      productUrl?: string | null;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      if (ctaDestination === "pdp" && selectedVariant) {
        // PDP mode: open the product detail page directly, attaching the
        // variant and (optionally) the discount code as query params.
        // Skips the headless Shopify-cart path (redirectToWebsiteWithDiscountCode)
        // so we don't end up at /cart/{id}:1.
        const pdpUrl = buildShopifyPdpUrl({
          storeUrl: returnAppSettings.storeUrl,
          handle: selectedVariant.handle,
          onlineStoreUrl: selectedVariant.productUrl ?? null,
          variantId: selectedVariant.id,
          discountCode,
          utmParams: {
            utm_source: "redo",
            utm_medium: "order_tracking",
            utm_content: "upsell",
            utm_term: order.internalMongoId,
          },
        });
        const w = open(pdpUrl);
        if (w) {
          w.onunload = () => {
            if (w.closed) {
              // @ts-expect-error: strict-null-checks
              selectWindow.current = undefined;
            }
          };
          selectWindow.current = w;
        }
        return;
      }

      redirectToWebsiteWithDiscountCode?.({
        utmSource: "redo",
        utmMedium: "order_tracking",
        utmContent: "upsell",
        utmTerm: order.internalMongoId,
        settings: returnAppSettings,
        window: selectWindow,
        discountCode,
        selectedProduct:
          redirectUrl && selectedVariant
            ? { url: redirectUrl, variantId: selectedVariant.id }
            : undefined,
      });
    },
  };
}

function buildShopifyPdpUrl({
  storeUrl,
  handle,
  onlineStoreUrl,
  variantId,
  discountCode,
  utmParams,
}: {
  storeUrl: string;
  handle: string;
  onlineStoreUrl: string | null;
  variantId: string;
  discountCode?: string;
  utmParams?: Record<string, string>;
}): string {
  // Prefer the canonical onlineStoreUrl when Shopify provides one, since it
  // handles custom domains. Fall back to the store's configured domain + handle
  // for published-but-no-onlineStoreUrl and draft/password-protected products.
  const base = onlineStoreUrl ?? `https://${storeUrl}/products/${handle}`;
  const url = new URL(base);
  url.searchParams.set("variant", variantId);
  if (discountCode) {
    url.searchParams.set("discount", discountCode);
  }
  if (utmParams) {
    for (const [key, value] of Object.entries(utmParams)) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

function createBigCommerceRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const url = selectedVariant
        ? new URL(
            `https://${returnAppSettings.storeUrl}${selectedVariant.handle}`,
          )
        : new URL(`https://${returnAppSettings.storeUrl}`);

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}

function createCommentsoldRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
      productUrl?: string | null;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const url = (() => {
        if (!selectedVariant) {
          return new URL(`https://${returnAppSettings.storeUrl}`);
        }
        if (selectedVariant.productUrl) {
          const url = new URL(`${selectedVariant.productUrl}`);
          url.search = `${
            url.search ? url.search + "&" : "?"
          }variant=${selectedVariant.id}`;
          return url;
        } else {
          throw new Error("Missing url for commentsold upsell redirect");
        }
      })();

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}

function createCommerceCloudRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
  redirectUrl,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const isCanadianOrder = order.shippingAddress?.countryCode === "CA";

      let baseStoreUrl = returnAppSettings.storeUrl;

      if (TAYLORMADE_TEAMS.includes(order.teamId) && isCanadianOrder) {
        baseStoreUrl = "www.taylormadegolf.ca";
      }
      if (SUNDAYRED_TEAMS.includes(order.teamId) && isCanadianOrder) {
        baseStoreUrl = "www.sundayred.ca";
      }

      const url = selectedVariant
        ? new URL(`https://${baseStoreUrl}/${selectedVariant.id}.html`)
        : new URL(`https://${baseStoreUrl}`);

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}
